// frontend/src/components/calendars/CalendarDisplay.js
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

export default function CalendarDisplay() {
  const [isLoading, setIsLoading] = useState(true);
  const [calendarData, setCalendarData] = useState({
    day: '',
    startDatetime: '',
    endDatetime: '',
    eventDescription: '',
    isTradingEnabled: true,
  });
  const [message, setMessage] = useState('');
  const [todayStr, setTodayStr] = useState(''); // 오늘 날짜(YYYY-MM-DD)

  const intervalRef = useRef(null);

  useEffect(() => {
    // 1) 오늘 날짜 계산
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const dayString = `${year}-${month}-${day}`;
    setTodayStr(dayString);

    // 2) 첫 fetch
    fetchCalendar(dayString);

    // 3) 1분10초마다 fetchCalendar
    intervalRef.current = setInterval(() => {
      fetchCalendar(dayString);
    }, 70000); // 1분(60000ms)

    // cleanup: 컴포넌트 언마운트 시 interval 해제
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const fetchCalendar = async (dayStr) => {
    setIsLoading(true);
    setMessage('');

    try {
      // GET /data/trading_calendar/{YYYY-MM-DD}
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/data/trading_calendar/${dayStr}`
      );

      // 백엔드 응답: { id, day, start_datetime, end_datetime, event_description, is_trading_enabled }
      const event = response.data;
      const now = new Date();
      const day = new Date(event.day);
      const start = new Date(event.start_datetime);
      const end = new Date(event.end_datetime);
      const description = event.event_description;
      const isTradingEnabled = event.is_trading_enabled;

      if (start <= now && now < end) {
        setCalendarData({
          day: day,
          startDatetime: start,
          endDatetime: end,
          eventDescription: description,
          isTradingEnabled: isTradingEnabled,
        });
      } else {
        // 현재 시각이 구간 밖 -> 거래 가능
        setCalendarData({
          day: '',
          startDatetime: '',
          endDatetime: '',
          eventDescription: '',
          isTradingEnabled: true,
        });
      }

      setIsLoading(false);
    } catch (error) {
      // 없는 경우(404) -> 레코드 없으면 거래 가능
      if (error.response && error.response.status === 404) {
        setCalendarData({
          day: '',
          startDatetime: '',
          endDatetime: '',
          eventDescription: '',
          isTradingEnabled: true,
        });
        setIsLoading(false);
        return;
      }
      // 다른 오류
      let errMsg = '';
      if (error.response && error.response.data) {
        errMsg = JSON.stringify(error.response.data);
      } else {
        errMsg = error.message;
      }
      setMessage('조회 실패: ' + errMsg);
      setCalendarData({
        day: '',
        startDatetime: '',
        endDatetime: '',
        eventDescription: '',
        isTradingEnabled: true,
      });
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {message && <p className="text-sm text-neutral-600">{message}</p>}
      {calendarData.isTradingEnabled ? (
        <div className="bg-green-500 border border-neutral-300 w-[17rem] h-[5rem] text-center flex items-center">
          <p className="mx-auto text-[1.3rem] font-bold text-white leading-none">
            거래 가능
          </p>
        </div>
      ) : (
        <div className="bg-slate-800 border border-neutral-300 w-[17rem] h-[5rem] text-center flex flex-col items-center justify-center gap-1">
          {/* 가로 배치 */}
          <div className="flex flex-row items-center justify-center gap-8">
            <p className="text-[1.3rem] font-bold text-white leading-snug">
              거래 불가
            </p>

            <p className="text-[wheat] leading-snug">
              {calendarData.startDatetime instanceof Date
                ? calendarData.startDatetime.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })
                : calendarData.startDatetime}
              {' ~ '}
              {calendarData.endDatetime instanceof Date
                ? calendarData.endDatetime.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })
                : calendarData.endDatetime}
            </p>
          </div>

          {/* 줄 바꿔 세로로 아래 배치 */}
          <p className="text-[1rem] font-bold text-[wheat] leading-snug">
            {calendarData.eventDescription}
          </p>
        </div>
      )}
    </div>
  );
}
